import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { deckService } from "@/services/firestore";
import { importService } from "@/services/importService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2, Upload, FileArchive } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { Progress } from "@/components/ui/progress";
import { useRef } from "react";

export default function NewDeckPage() {
  const { user } = useAuthStore();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setLoading(true);
    try {
      const docRef = await deckService.createDeck(user.uid, {
        title,
        description,
        isPublic
      });
      navigate(`/decks/${docRef}`);
    } catch (error) {
      console.error("Error creating deck:", error);
      alert("Failed to create deck");
    } finally {
      setLoading(false);
    }
  };

  const handleZipImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setImporting(true);
    setImportProgress(0);
    setImportStatus("Đang khởi tạo...");
    try {
      const result = await importService.importDeckFromZip(file, (current, total, status) => {
        setImportProgress(current);
        setImportStatus(status);
      });
      navigate(`/decks/${result.deckId}`);
    } catch (error: any) {
      console.error("Error importing ZIP:", error);
      alert(error.message || "Failed to import ZIP file");
    } finally {
      setImporting(false);
      setImportProgress(0);
      setImportStatus("");
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 md:py-12 px-4 md:px-6">
      <Helmet>
        <title>Tạo bộ thẻ mới | Flashcard Platform</title>
      </Helmet>

      <Link 
        to="/decks" 
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "mb-6")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Quay lại danh sách
      </Link>

      <Card className="rounded-3xl border-2">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Tạo bộ thẻ mới</CardTitle>
          <CardDescription>
            Tạo thủ công hoặc tải lên tệp ZIP để tạo nhanh.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* ZIP Import Section */}
          <div className="p-6 rounded-2xl border-2 border-dashed border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FileArchive className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="font-bold text-lg">Nhập nhanh từ ZIP</h3>
              <p className="text-sm text-muted-foreground">
                Tải lên tệp ZIP chứa metadata.json và thư mục assets.
              </p>
            </div>
            <input 
              type="file" 
              accept=".zip" 
              className="hidden" 
              ref={fileInputRef}
              onChange={handleZipImport}
            />
            <Button 
              type="button" 
              variant="outline" 
              className="rounded-xl font-bold border-2 hover:bg-background"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" /> Chọn tệp ZIP
                </>
              )}
            </Button>

            {importing && (
              <div className="space-y-3 pt-4 text-left max-w-sm mx-auto">
                <div className="flex justify-between items-end px-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary animate-pulse">
                    {importStatus}
                  </span>
                  <span className="text-[10px] font-bold text-muted-foreground">
                    {importProgress}%
                  </span>
                </div>
                <Progress value={importProgress} className="h-2 rounded-full" />
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground font-bold">Hoặc tạo thủ công</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="font-bold">Tiêu đề bộ thẻ</Label>
              <Input 
                id="title" 
                placeholder="Ví dụ: Từ vựng Tiếng Anh, JLPT N5, React Hooks..." 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-12 rounded-xl"
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="font-bold">Mô tả (Tùy chọn)</Label>
              <Textarea 
                id="description" 
                placeholder="Bộ thẻ này nói về cái gì?" 
                className="min-h-[100px] rounded-xl"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-2xl border bg-muted/30">
              <div className="space-y-0.5">
                <Label htmlFor="public" className="font-bold">Công khai bộ thẻ</Label>
                <p className="text-sm text-muted-foreground">
                  Cho phép người dùng khác tìm thấy và sao chép bộ thẻ này.
                </p>
              </div>
              <Switch 
                id="public" 
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
            </div>

            <div className="flex gap-4 pt-2">
              <Button type="submit" className="flex-1 h-12 rounded-xl font-bold" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Tạo bộ thẻ"}
              </Button>
              <Link 
                to="/decks" 
                className={cn(buttonVariants({ variant: "outline" }), "flex-1 h-12 rounded-xl font-bold")}
              >
                Hủy bỏ
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
